// Services
import { HttpService } from "@mgs/ci2019-common/src/services/HttpService";
// Store
import { Store } from "#/store";
// Models
import { FahrzeugModell } from "#/common/models/model_selection/FahrzeugModell";
import { TypeSelectionResult } from "#/common/models/type_selection/TypeSelectionResult";
import { CatalogSelectionResult } from "#/common/models/type_selection/CatalogSelectionResult";
import { KatalogData } from "#/common/models/katalog_data/KatalogData";
import { ConfigSelectionResult } from "../common/models/type_selection/ConfigSelectionResult";

export class KatalogService {
  private static _instance;

  public static get instance() {
    if (!this._instance) {
      this._instance = new KatalogService();
    }
    return this._instance;
  }

  private constructor() {}

  private readonly http: HttpService = HttpService.instance;

  public async getModelSelection(
    sparte: string,
    state: string
  ): Promise<FahrzeugModell[]> {
    const response = await this.http.get<FahrzeugModell[]>(
      Store.instance.ui.katalogApiUrl + `modelselection/${sparte}/${state}`
    );

    return response.body!;
  }

  public async getConfigSelection(
    klasse: number,
    config: object
  ): Promise<ConfigSelectionResult> {
    const response = await this.http.post<ConfigSelectionResult>(
      Store.instance.ui.katalogApiUrl + `configselection/${klasse}`,
      {
        config: config,
      }
    );

    return response.body!;
  }

  public async getTypeSelection(
    klasse: number,
    config: object,
    filter: object,
    completeFilter: object
  ): Promise<TypeSelectionResult> {
    const response = await this.http.post<TypeSelectionResult>(
      Store.instance.ui.katalogApiUrl + `typeselection/${klasse}`,
      {
        config: config,
        filter: filter,
        completeFilter: completeFilter,
      }
    );

    return response.body!;
  }

  public async getCatalogSelection(
    klasse: number,
    filter: object,
    completeFilter: object
  ): Promise<CatalogSelectionResult> {
    const response = await this.http.post<CatalogSelectionResult>(
      Store.instance.ui.katalogApiUrl + `catalogs/${klasse}`,
      {
        filter: filter,
        completeFilter: completeFilter,
      }
    );

    return response.body!;
  }

  public async updateSession(token: string, data: object): Promise<object> {
    const response = await this.http.post<object>(
      Store.instance.ui.katalogApiUrl + `session/${token}`,
      data
    );

    return response.body!;
  }

  public async getSession(token: string): Promise<object> {
    const response = await this.http.get<object>(
      Store.instance.ui.katalogApiUrl + `session/${token}`
    );

    return response.body!;
  }

  public async getData(
    klasse: number | null,
    catalog: string,
    filter: object,
    filterCatalog: object,
    token?: string
  ): Promise<KatalogData> {
    let queryParams = {
      region: Store.instance.ui.appCurrentCountry,
    };

    if (klasse != null) {
      queryParams["klasse"] = klasse;
    }

    if (token) queryParams["token"] = token;

    const response = await this.http.post<KatalogData>(
      Store.instance.ui.katalogApiUrl + `data/${catalog}`,
      { filter, filterCatalog },
      {
        params: queryParams,
      }
    );

    return response.body!;
  }

  public getDownloadFilesUrl(klasse: number, filenames: string[]) {
    let query = filenames
      .map((filename) => {
        return (
          encodeURIComponent("filenames[]") + "=" + encodeURIComponent(filename)
        );
      })
      .join("&");

    return (
      Store.instance.ui.katalogApiUrl +
      `catalogs/ar2/vehicle-classes/${klasse}/downloads` +
      "?" +
      query
    );
  }
  // === Special Equipment ===
  public async getSpecialEquipment(
    filterGroups: string[],
    filter: object | undefined,
    resultConfig: any,
    token: string
  ): Promise<any> {
    let queryParams = {
      filterGroups: filterGroups,
      resultConfig: resultConfig,
    };

    // this is neccessary because the API wants the queryparameter like this 'filter[123]=abc'
    // with axios versions 1.0.0 and up it can be done with 'paramsSerializer: {indexes: true}'
    let filterString = "";
    if (filter != null) {
      Object.entries(filter).forEach(([key, value], index) => {
        filterString += (index > 0 ? "&" : "") + "filter[" + key + "]=" + value;
      });
    }

    const response = await this.http.get<any>(
      Store.instance.ui.catalogApiUrl +
        `special-equipment-catalog/${token}?${filterString}`,
      {
        params: queryParams,
      }
    );

    return response.body!;
  }

  public async downloadPtoDrawing(
    model: string,
    code: string[],
    filetype: string = "pdf",
    date: string
  ): Promise<any> {
    try {
      const response = await this.http.post<any>(
        Store.instance.ui.cadvisApiUrl + `drawing_pto/${filetype}`,
        {
          source: "ABH",
          baumuster: model,
          code: code,
          tires: [""],
          production_date: date,
        },
        { responseType: "blob" }
      );
      if (response.headers["content-type"] === "text/html; charset=UTF-8") {
        response.body
          .text()
          .then((resolve) => Store.instance.ui.error(resolve.split("\n")[0]));
      } else {
        let filename = decodeURIComponent(
          response.headers["content-disposition"].split("filename=")[1]
        );
        filename = filename.replaceAll(/['"]+/g, "");
        this.downloadBlob(
          response.body,
          filename,
          response.headers["content-type"]
        );
      }
      return response.body!;
    } catch (e) {
      Store.instance.ui.error(e.message);
    }
    return false;
  }

  downloadBlob(resBlob: Blob, filename: string, type: string) {
    const blob = new Blob([resBlob], { type: type });
    const blobUrl = URL.createObjectURL(blob);

    const downloadLink = document.createElement("a");
    downloadLink.href = blobUrl;
    downloadLink.download = filename; // Set the desired filename and extension

    document.body.appendChild(downloadLink);
    downloadLink.click();

    // Clean up the created URL and anchor element
    URL.revokeObjectURL(blobUrl);
    document.body.removeChild(downloadLink);
  }

  // === FILE CLOUD ===
  public async getFiles(category: string): Promise<any> {
    const response = await this.http.get<any>(
      Store.instance.ui.katalogApiUrl +
        `file-cloud/categories/${category}/files`
    );

    return response.body!;
  }

  public async createFile(category: string, file: any): Promise<any> {
    const response = await this.http.post<any>(
      Store.instance.ui.katalogApiUrl +
        `file-cloud/categories/${category}/files`,
      this.http.createFormData({ file }),
      { timeout: 0 }
    );

    return response.body!;
  }

  public async deleteFile(category: string, fileId: string): Promise<any> {
    const response = await this.http.delete<any>(
      Store.instance.ui.katalogApiUrl +
        `file-cloud/categories/${category}/files/${fileId}`
    );

    return response.body!;
  }

  public async getFile(category: string, id: string): Promise<any> {
    return await this.http.get<any>(
      Store.instance.ui.katalogApiUrl +
        `file-cloud/categories/${category}/files/${id}`
    );
  }

  public async updateFile(
    category: string,
    id: string,
    file: any
  ): Promise<any> {
    const response = await this.http.post<any>(
      Store.instance.ui.katalogApiUrl +
        `file-cloud/categories/${category}/files/${id}`,
      this.http.createFormData({ file }),
      { timeout: 0 }
    );

    return response.body!;
  }
  // === FILE CLOUD END ===

  public async finArchivAbfragen(fin: string) {
    try {
      const response = await this.http.get<any>(
        Store.instance.ui.katalogApiUrl + `fin-archiv-abfragen`,
        {
          params: {
            fin,
          },
        }
      );

      return response.body!;
    } catch (e) {
      return e.body;
    }
  }
}
